import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from '@phosphor-icons/react';


export function EditJobOrder() {
  const { jo_number } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [specialGlassTypes, setSpecialGlassTypes] = useState([]);
  const [formData, setFormData] = useState({
    jo_date: '',
    customer_name: '',
    customer_mobile: '',
    work_location: '',
    quotation_no: '',
    executive_name: '',
    total_sqft: '',
    no_of_windows: '',
    delivery_date: '',
    profile_type: 'White',
    glass_type: 'Normal',
  });

  const fetchJobOrder = useCallback(async () => {
    try {
      const [joRes, glassRes] = await Promise.all([
        api.get(`/api/job-orders/${jo_number}`),
        api.get(`/api/dropdown-config?category=glass_type_special`)
      ]);
      const jo = joRes.data;
      setFormData({
        jo_date: jo.jo_date || '',
        customer_name: jo.customer_name || '',
        customer_mobile: jo.customer_mobile || '',
        work_location: jo.work_location || '',
        quotation_no: jo.quotation_no || '',
        executive_name: jo.executive_name || '',
        total_sqft: jo.total_sqft || '',
        no_of_windows: jo.no_of_windows || '',
        delivery_date: jo.delivery_date || jo.confirmed_delivery_date || '',
        profile_type: jo.profile_type || 'White',
        glass_type: jo.glass_type || 'Normal',
      });
      setSpecialGlassTypes(glassRes.data.values || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load job order');
      navigate('/job-orders');
    } finally {
      setFetching(false);
    }
  }, [jo_number, navigate]);

  useEffect(() => {
    fetchJobOrder();
  }, [fetchJobOrder]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        total_sqft: parseFloat(formData.total_sqft),
        no_of_windows: parseInt(formData.no_of_windows),
      };
      await api.put(`/api/job-orders/${jo_number}`, payload);
      toast.success('Job Order updated successfully');
      navigate(`/job-orders/${jo_number}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job order');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Layout>
        <div className="text-center py-12">Loading job order...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)} className="h-11">
            <ArrowLeft size={20} weight="bold" className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Edit Job Order #{jo_number}</h1>
            <p className="text-sm text-zinc-500 mt-2">Modify job order details</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Details */}
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Job Order Details</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Job Order No</Label>
                    <Input
                      data-testid="edit-jo-number"
                      value={jo_number}
                      disabled
                      className="h-11 bg-zinc-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="jo_date" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">JO Date *</Label>
                    <Input
                      id="jo_date"
                      data-testid="edit-jo-date"
                      type="date"
                      required
                      value={formData.jo_date}
                      onChange={(e) => handleChange('jo_date', e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Details */}
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Customer Name *</Label>
                    <Input
                      id="customer_name"
                      data-testid="edit-customer-name"
                      required
                      value={formData.customer_name}
                      onChange={(e) => handleChange('customer_name', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_mobile" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Mobile No *</Label>
                    <Input
                      id="customer_mobile"
                      data-testid="edit-customer-mobile"
                      required
                      value={formData.customer_mobile}
                      onChange={(e) => handleChange('customer_mobile', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="work_location" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Work Location *</Label>
                    <Textarea
                      id="work_location"
                      data-testid="edit-work-location"
                      required
                      value={formData.work_location}
                      onChange={(e) => handleChange('work_location', e.target.value)}
                      className="min-h-20"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Specifications */}
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Window Specifications</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="quotation_no" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Quotation No *</Label>
                    <Input
                      id="quotation_no"
                      data-testid="edit-quotation-no"
                      required
                      value={formData.quotation_no}
                      onChange={(e) => handleChange('quotation_no', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="executive_name" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Executive Name *</Label>
                    <Input
                      id="executive_name"
                      data-testid="edit-executive-name"
                      required
                      value={formData.executive_name}
                      onChange={(e) => handleChange('executive_name', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total_sqft" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft *</Label>
                    <Input
                      id="total_sqft"
                      data-testid="edit-total-sqft"
                      type="number"
                      step="0.01"
                      required
                      value={formData.total_sqft}
                      onChange={(e) => handleChange('total_sqft', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="no_of_windows" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">No of Windows *</Label>
                    <Input
                      id="no_of_windows"
                      data-testid="edit-no-of-windows"
                      type="number"
                      required
                      value={formData.no_of_windows}
                      onChange={(e) => handleChange('no_of_windows', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_date" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery Date *</Label>
                    <Input
                      id="delivery_date"
                      data-testid="edit-delivery-date"
                      type="date"
                      required
                      value={formData.delivery_date}
                      onChange={(e) => handleChange('delivery_date', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile_type" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Profile Type *</Label>
                    <Select value={formData.profile_type} onValueChange={(val) => handleChange('profile_type', val)}>
                      <SelectTrigger data-testid="edit-profile-type" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="White">White</SelectItem>
                        <SelectItem value="Colour">Colour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="glass_type" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Glass Type *</Label>
                    <Select value={formData.glass_type} onValueChange={(val) => handleChange('glass_type', val)}>
                      <SelectTrigger data-testid="edit-glass-type" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="Special">Special</SelectItem>
                        {specialGlassTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button
                type="submit"
                data-testid="save-edit-button"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-8"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/job-orders/${jo_number}`)}
                className="h-11 px-8"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}
